import React, { useCallback } from "react";
import { useReactFlow } from "reactflow";
import { ZoomIn, ZoomOut, Maximize2, Crosshair, Minus, Plus } from "lucide-react";
import type { GraphLocale } from "./graphCopy";
import { formatGraphMessage, graphMessage } from "./graphCopy";

/**
 * Fallback dimensions for the issue node when React Flow has not yet measured
 * the rendered DOM. Mirrors the constants in the layout engine so we can
 * recenter on the midpoint of a node before it has a measured `width`/`height`.
 */
const DEFAULT_NODE_WIDTH_PX = 200;
const DEFAULT_NODE_HEIGHT_PX = 90;

/** Zoom used when recentering on the root node without an explicit target zoom. */
const ROOT_RECENTER_ZOOM_FALLBACK = 1;
const MIN_RECENTER_ZOOM = 0.2;
const MAX_RECENTER_ZOOM = 2;

export type GraphFlowControlsOrientation = "horizontal" | "vertical";

interface GraphFlowControlsProps {
  depth: number;
  onDepthUp: () => void;
  onDepthDown: () => void;
  minDepth?: number;
  maxDepth?: number;
  className?: string;
  showDepthControls?: boolean;
  locale?: GraphLocale;
  /**
   * Render direction for the control cluster. `horizontal` is the desktop
   * default (bottom-centered). `vertical` is used on mobile where the cluster
   * is pinned to the left edge and stacked for thumb reach.
   */
  orientation?: GraphFlowControlsOrientation;
  /**
   * React Flow node id (as a string) to center on when the user taps
   * Recenter. When omitted (or the node is not found in the flow graph), the
   * handler falls back to `fitView` so non-rooted graphs still get a useful
   * "reset view" action.
   */
  rootNodeId?: string | null;
}

/**
 * Control center for the graph: zoom, recenter, fit view, depth. Supports a
 * horizontal (desktop bottom bar) and vertical (mobile left rail) layout so
 * the same component can cover every `GraphCanvas` surface.
 */
const GraphFlowControls: React.FC<GraphFlowControlsProps> = ({
  depth,
  onDepthUp,
  onDepthDown,
  minDepth = 1,
  maxDepth = 5,
  className = "",
  showDepthControls = true,
  locale = "en-US",
  orientation = "horizontal",
  rootNodeId,
}) => {
  const { zoomIn, zoomOut, fitView, setCenter, getNode, getZoom } = useReactFlow();

  const isVertical = orientation === "vertical";

  const handleZoomIn = useCallback(() => {
    void zoomIn();
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    void zoomOut();
  }, [zoomOut]);

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 300 });
  }, [fitView]);

  /**
   * Center the viewport on the root/starting issue when one is known. We
   * target the node midpoint (position is top-left in React Flow) and clamp
   * the current zoom to the allowed range so the recenter gesture never
   * leaves the user at an unusable scale. When no root is known we fall back
   * to `fitView` which is a reasonable "reset" across all graph surfaces.
   */
  const handleRecenter = useCallback(() => {
    if (rootNodeId) {
      const node = getNode(String(rootNodeId));
      if (node && node.position) {
        const nodeWidth = typeof node.width === "number" && node.width > 0 ? node.width : DEFAULT_NODE_WIDTH_PX;
        const nodeHeight =
          typeof node.height === "number" && node.height > 0 ? node.height : DEFAULT_NODE_HEIGHT_PX;
        const currentZoom = getZoom();
        const nextZoom = Math.min(
          MAX_RECENTER_ZOOM,
          Math.max(MIN_RECENTER_ZOOM, currentZoom || ROOT_RECENTER_ZOOM_FALLBACK)
        );
        setCenter(node.position.x + nodeWidth / 2, node.position.y + nodeHeight / 2, {
          zoom: nextZoom,
          duration: 300,
        });
        return;
      }
    }
    fitView({ padding: 0.2, duration: 300 });
  }, [fitView, getNode, getZoom, rootNodeId, setCenter]);

  const buttonBaseClass =
    "flex items-center justify-center size-8 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text dark:text-eoe-dark-text transition-colors";

  const dividerClass = isVertical
    ? "graph-flow-controls-divider h-px w-6 bg-eoe-border-main dark:bg-eoe-dark-border-main"
    : "graph-flow-controls-divider w-px h-6 bg-eoe-border-main dark:bg-eoe-dark-border-main";

  const zoomOutButton = (
    <button
      key="zoom-out"
      type="button"
      onClick={handleZoomOut}
      className={`graph-flow-controls-zoom-out ${buttonBaseClass}`}
      aria-label={graphMessage(locale, "zoomOut")}
      title={graphMessage(locale, "zoomOut")}
    >
      <ZoomOut className="size-4" aria-hidden />
    </button>
  );

  const zoomInButton = (
    <button
      key="zoom-in"
      type="button"
      onClick={handleZoomIn}
      className={`graph-flow-controls-zoom-in ${buttonBaseClass}`}
      aria-label={graphMessage(locale, "zoomIn")}
      title={graphMessage(locale, "zoomIn")}
    >
      <ZoomIn className="size-4" aria-hidden />
    </button>
  );

  const recenterButton = (
    <button
      key="recenter"
      type="button"
      onClick={handleRecenter}
      className={`graph-flow-controls-recenter ${buttonBaseClass}`}
      aria-label={graphMessage(locale, "recenter")}
      title={graphMessage(locale, "recenter")}
    >
      <Crosshair className="size-4" aria-hidden />
    </button>
  );

  const fitButton = (
    <button
      key="fit"
      type="button"
      onClick={handleFitView}
      className={`graph-flow-controls-fit ${buttonBaseClass}`}
      aria-label={graphMessage(locale, "zoomToFit")}
      title={graphMessage(locale, "zoomToContent")}
    >
      <Maximize2 className="size-4" aria-hidden />
    </button>
  );

  const depthDownButton = (
    <button
      key="depth-down"
      type="button"
      onClick={onDepthDown}
      disabled={depth <= minDepth}
      className={`graph-flow-controls-depth-down flex items-center justify-center size-8 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover disabled:opacity-50 disabled:cursor-not-allowed text-eoe-text dark:text-eoe-dark-text transition-colors`}
      aria-label={graphMessage(locale, "decreaseDepth")}
    >
      <Minus className="size-4" aria-hidden />
    </button>
  );

  const depthValue = (
    <span
      key="depth-value"
      className="graph-flow-controls-depth-value min-w-[2rem] text-center text-sm font-semibold text-eoe-text dark:text-eoe-dark-text"
      aria-live="polite"
      aria-label={formatGraphMessage(locale, "depthAria", { depth })}
    >
      {depth}
    </span>
  );

  const depthUpButton = (
    <button
      key="depth-up"
      type="button"
      onClick={onDepthUp}
      disabled={depth >= maxDepth}
      className={`graph-flow-controls-depth-up flex items-center justify-center size-8 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover disabled:opacity-50 disabled:cursor-not-allowed text-eoe-text dark:text-eoe-dark-text transition-colors`}
      aria-label={graphMessage(locale, "increaseDepth")}
    >
      <Plus className="size-4" aria-hidden />
    </button>
  );

  /**
   * Vertical orientation flips the +/- style controls so the "increase"
   * affordance (zoom-in, depth-up) sits on top — matching familiar patterns
   * on native map and media apps. Horizontal keeps the minus-on-left,
   * plus-on-right convention.
   */
  const zoomCluster = isVertical ? [zoomInButton, zoomOutButton] : [zoomOutButton, zoomInButton];
  const panCluster = [recenterButton, fitButton];
  const depthCluster = isVertical
    ? [depthUpButton, depthValue, depthDownButton]
    : [depthDownButton, depthValue, depthUpButton];

  const containerDirection = isVertical ? "flex-col" : "flex-row";
  const containerPadding = isVertical ? "px-2 py-3" : "px-3 py-2";
  const depthGroupDirection = isVertical ? "flex-col" : "flex-row";

  return (
    <div
      className={`graph-flow-controls flex ${containerDirection} items-center gap-2 rounded-full ${containerPadding} bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main shadow-sm ${className}`}
      role="toolbar"
      aria-orientation={isVertical ? "vertical" : "horizontal"}
    >
      {zoomCluster}

      <div className={dividerClass} aria-hidden />

      {panCluster}

      {showDepthControls ? (
        <>
          <div className={dividerClass} aria-hidden />
          <div className={`graph-flow-controls-depth flex ${depthGroupDirection} items-center gap-1`}>
            {depthCluster}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default GraphFlowControls;
